import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "@/lib/authStore";
import { getProfileSessions, getProfileStats, MEDDPICC_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Play, LogOut, BarChart3, FileText, RotateCcw } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const StatCard = ({ label, value, testId }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-5 pb-4">
      <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1" data-testid={testId}>{value}</p>
    </CardContent>
  </Card>
);

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) {
      navigate("/login");
      return;
    }
    if (user === undefined) return;
    Promise.all([getProfileStats(), getProfileSessions()])
      .then(([statsRes, sessRes]) => {
        setStats(statsRes.data.stats);
        setSessions(sessRes.data.sessions);
      })
      .catch((e) => {
        if (e.response?.status === 401) navigate("/login");
      })
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  if (user === undefined || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const meddpiccData = stats
    ? Object.entries(stats.avg_meddpicc).map(([k, v]) => ({
        dim: k, name: MEDDPICC_LABELS[k]?.name || k, value: Math.round(v * 100), fill: MEDDPICC_LABELS[k]?.color,
      }))
    : [];

  return (
    <div className="min-h-screen bg-slate-50/50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50 accent-border-top">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Deal Velocity · Akamai</h1>
              <p className="text-xs text-slate-400">My Profile</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/dashboard">
              <Button variant="outline" size="sm" className="border-slate-200" data-testid="nav-dashboard-btn">
                <BarChart3 className="w-4 h-4 mr-1" /> Team Dashboard
              </Button>
            </Link>
            <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => navigate("/")}
              data-testid="profile-new-session-btn">
              <Play className="w-4 h-4 mr-1" /> New Session
            </Button>
            <Button variant="outline" size="sm" onClick={onLogout}
              className="text-slate-500 border-slate-200" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <div data-testid="profile-header">
          <h2 className="text-2xl font-bold text-slate-900">{user?.name}</h2>
          <p className="text-sm text-slate-400">{user?.email}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Sessions" value={stats?.total_sessions ?? 0} testId="stat-total-sessions" />
          <StatCard label="Completed" value={stats?.completed_sessions ?? 0} testId="stat-completed" />
          <StatCard label="Avg Trust" value={stats?.avg_trust ?? "—"} testId="stat-avg-trust" />
          <StatCard label="Avg DVI" value={stats?.avg_dvi != null ? `${stats.avg_dvi}` : "—"} testId="stat-avg-dvi" />
          <StatCard label="Best DVI" value={stats?.best_dvi != null ? `${stats.best_dvi}` : "—"} testId="stat-best-dvi" />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Performance Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.trend?.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={stats.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="session" tick={{ fontSize: 11 }} label={{ value: "Session", position: "insideBottom", offset: -2, fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="dvi" name="DVI" stroke="#E01C4A" strokeWidth={2} dot />
                    <Line type="monotone" dataKey="trust" name="Trust" stroke="#0F3559" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 py-12 text-center">Complete a session to see your trend.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Average MEDDPICC Coverage</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={meddpiccData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="dim" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip formatter={(v, n, p) => [`${v}%`, p.payload.name]} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Session History</CardTitle>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-sm text-slate-400 mb-4">No sessions yet. Start your first simulation!</p>
                <Button className="bg-primary hover:bg-primary/90" onClick={() => navigate("/")} data-testid="empty-start-btn">
                  <Play className="w-4 h-4 mr-1" /> Start Simulation
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="session-history-list">
                {sessions.map((s) => (
                  <div key={s.session_id} className="py-3 flex items-center justify-between gap-4 flex-wrap"
                    data-testid={`session-row-${s.session_id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-800">
                          {(s.started_at || "").slice(0, 10)}
                        </span>
                        <Badge variant="outline" className="text-xs border-slate-200">
                          {s.is_active ? "In progress" : s.end_reason?.replace(/_/g, " ") || "ended"}
                        </Badge>
                        {s.performance_band && (
                          <Badge className="text-xs bg-primary/10 text-primary border-0">{s.performance_band}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {s.turns} turns · Trust {s.trust_score} · Phase {s.phase}
                        {s.dvi != null && ` · DVI ${Math.round(s.dvi * 100)}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {s.is_active ? (
                        <Button size="sm" variant="outline" className="border-primary/30 text-primary"
                          onClick={() => navigate(`/simulation/${s.session_id}`)} data-testid="resume-session-btn">
                          Resume
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" className="border-slate-200"
                            onClick={() => navigate(`/report/${s.session_id}`)} data-testid="view-report-btn">
                            <FileText className="w-3.5 h-3.5 mr-1" /> Report
                          </Button>
                          <Button size="sm" variant="outline" className="border-slate-200"
                            onClick={() => navigate(`/replay/${s.session_id}`)} data-testid="view-replay-btn">
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Replay
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
